import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, Languages } from "lucide-react";
import { LanguageContext } from "../App";

const DISMISS_KEY = "aiw_lang_banner_dismissed";
const CHOICE_KEY = "aiw_lang";

const COPY = {
  es: {
    prompt: "¿Prefieres ver el sitio en Español?",
    accept: "Ver en Español",
    dismiss: "No, gracias",
  },
  tr: {
    prompt: "Siteyi Türkçe görmek ister misiniz?",
    accept: "Türkçe'ye Geç",
    dismiss: "Hayır, teşekkürler",
  },
};

const KNOWN_PATHS = new Set([
  "/", "/audit", "/discover", "/dental", "/restaurant", "/hvac", "/healthcare",
  "/legal", "/real-estate", "/retail", "/shopify", "/construction",
  "/accounting", "/saas", "/auto-repair", "/med-spa",
]);

function detectBrowserLang() {
  const raw = (navigator.language || navigator.userLanguage || "").toLowerCase();
  if (raw.startsWith("es")) return "es";
  if (raw.startsWith("tr")) return "tr";
  return null;
}

export default function LanguageSuggestionBanner() {
  const { setLang } = useContext(LanguageContext);
  const location = useLocation();
  const navigate = useNavigate();

  const suggested = useMemo(() => {
    if (typeof window === "undefined") return null;
    // Already dismissed or an explicit choice stored? skip.
    if (localStorage.getItem(DISMISS_KEY) === "1") return null;
    if (localStorage.getItem(CHOICE_KEY)) return null;
    // Only suggest when on an English URL (the /es/* and /tr/* routes are already localized).
    if (location.pathname.startsWith("/es/") || location.pathname.startsWith("/tr/")
        || location.pathname === "/es" || location.pathname === "/tr") {
      return null;
    }
    // Only suggest for paths we have translations for.
    if (!KNOWN_PATHS.has(location.pathname)) return null;
    return detectBrowserLang();
  }, [location.pathname]);

  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(!!suggested); }, [suggested]);

  if (!open || !suggested) return null;

  const copy = COPY[suggested];

  const accept = () => {
    setLang(suggested);
    const target = location.pathname === "/"
      ? `/${suggested}`
      : `/${suggested}${location.pathname}`;
    localStorage.setItem(CHOICE_KEY, suggested);
    setOpen(false);
    navigate(target);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setOpen(false);
  };

  return (
    <div className="fixed top-[72px] left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-lg">
      <div className="flex items-center gap-3 bg-slate-900/95 backdrop-blur-xl border border-blue-500/30 rounded-2xl px-4 py-3 shadow-2xl shadow-blue-500/10">
        <div className="w-8 h-8 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center flex-shrink-0">
          <Languages className="w-4 h-4 text-blue-400" />
        </div>
        <p className="text-sm text-white flex-1 leading-snug">{copy.prompt}</p>
        <button
          onClick={accept}
          className="text-xs font-semibold bg-blue-500 hover:bg-blue-400 text-slate-950 px-3 py-1.5 rounded-full flex-shrink-0 transition-colors"
        >
          {copy.accept}
        </button>
        <button
          onClick={dismiss}
          aria-label={copy.dismiss}
          className="text-slate-500 hover:text-slate-300 flex-shrink-0 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
